import * as React from 'react';
import {
  View,
  StyleSheet,
  Dimensions,
  EmitterSubscription,
  Modal,
  Platform
} from 'react-native';
import {
  GestureHandlerRootView,
  ScrollView
} from 'react-native-gesture-handler';
import RenderHtml, { defaultSystemFonts } from 'react-native-render-html';
import { translate } from 'secullum-i18n';
import { Button, CheckBox, getTestID } from 'secullum-react-native-ui';

const systemFonts = [...defaultSystemFonts, 'Roboto', 'Roboto-Medium'];

interface Props {
  visivel: boolean;
  html: string;
  altura: number;
  largura: number;
  onClose: (naoExibirNovamente: boolean) => void;
}

interface State {
  naoExibirNovamente: boolean;
}

class ModalPopup extends React.Component<Props, State> {
  state: State = {
    naoExibirNovamente: false
  };

  screenChangeDimensionsSubscription: EmitterSubscription | undefined;

  componentDidMount() {
    this.screenChangeDimensionsSubscription = Dimensions.addEventListener(
      'change',
      this.handleDimensionsChange
    );
  }

  componentWillUnmount() {
    if (this.screenChangeDimensionsSubscription) {
      this.screenChangeDimensionsSubscription.remove();
    }
  }

  handleDimensionsChange = () => {
    this.forceUpdate();
  };

  handleFechar = () => {
    const { naoExibirNovamente } = this.state;
    const { onClose } = this.props;

    onClose(naoExibirNovamente);
  };

  render() {
    const { visivel, html, altura, largura } = this.props;
    const { naoExibirNovamente } = this.state;

    const screen = Dimensions.get('window');
    const containerWidth =
      largura > 0 ? largura : Math.min(screen.width * 0.8, 400);
    const contentWidth = containerWidth - 32;
    const containerStyle = {
      ...styles.container,
      width: containerWidth,
      ...(altura > 0 ? { height: altura } : { maxHeight: screen.height * 0.85 })
    };
    const conteudoEstiloTamanho =
      altura > 0 ? styles.conteudoFlex : styles.conteudoShrink;

    return (
      // Diferente dos demais modais do app, aqui é usado o Modal nativo do react-native em vez
      // do da secullum-react-native-ui, pois esse último envolve todo o conteúdo (não só o
      // backdrop) em um TouchableWithoutFeedback. Como o Modal do RN abre em uma janela nativa
      // própria, esse Touchable acaba sendo ancestral do GestureHandlerRootView dentro dessa
      // mesma janela e intercepta o gesto de arrastar antes que o sistema nativo de gestos do
      // react-native-gesture-handler consiga capturá-lo, tornando a rolagem intermitente.
      <Modal
        visible={visivel}
        transparent
        animationType={Platform.OS === 'ios' ? 'none' : 'fade'}
        supportedOrientations={['landscape', 'portrait']}
      >
        <View style={styles.overlay}>
          <View
            style={containerStyle}
            nativeID="modal-popup-central"
            testID={getTestID('modal-popup-central')}
          >
            <GestureHandlerRootView
              style={[styles.conteudo, conteudoEstiloTamanho]}
            >
              {/* No web, navegadores tornam containers com scroll focáveis automaticamente e
                  desenham um contorno de foco escuro ao redor deles ao clicar/arrastar dentro,
                  daí a necessidade de remover o outline explicitamente aqui. */}
              <ScrollView
                style={[conteudoEstiloTamanho, { outline: 'none' } as any]}
              >
                {/* Foi necessário incluir o ignore, pois com a atualização do react-native para a versão 0.73
                    o tipo de retorno de algumas propriedades dos estilos mudou, antes eram strings, agora são DimensionValue.
                    A lib RenderHtml ainda não foi atualizada para usar essa nova tipagem. */}
                {/* @ts-ignore */}
                <RenderHtml
                  source={{ html }}
                  contentWidth={contentWidth}
                  systemFonts={systemFonts}
                  // No React 19, componentes funcionais não suportam mais defaultProps, e o
                  // react-native-render-html@6.3.4 depende desse mecanismo para habilitar o
                  // processamento de estilos inline por padrão. Sem passar explicitamente aqui,
                  // os atributos style="..." do HTML são ignorados silenciosamente.
                  enableCSSInlineProcessing
                />
              </ScrollView>
            </GestureHandlerRootView>
            <View style={styles.checkbox}>
              <CheckBox
                label={translate('Não exibir novamente')}
                value={naoExibirNovamente}
                onChange={value => this.setState({ naoExibirNovamente: value })}
                nativeID="popup-nao-exibir-novamente"
              />
            </View>
            <View style={styles.botoes}>
              <Button
                text={translate('Fechar')}
                onPress={this.handleFechar}
                nativeID="popup-btn-fechar"
                style={styles.botao}
              />
            </View>
          </View>
        </View>
      </Modal>
    );
  }
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(33, 33, 33, 0.7)'
  },
  container: {
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 16,
    overflow: 'hidden'
  },
  conteudo: {
    marginBottom: 12
  },
  conteudoFlex: {
    flex: 1
  },
  conteudoShrink: {
    flexShrink: 1,
    minHeight: 0
  },
  checkbox: {
    marginBottom: 16
  },
  botoes: {
    flexDirection: 'row'
  },
  botao: {
    alignSelf: 'flex-start',
    alignItems: 'center',
    borderColor: 'transparent',
    width: '100%'
  }
});

export default ModalPopup;
